import useTranslation from "@babackapp/translations/use-translation";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import { useShopFlag } from "@/hooks/use-flag";
import ColissimoInfoFrImage from "@/assets/infoFr.png";
import ColissimoInfoEnImage from "@/assets/infoEn.png";
import ColissimoInfoEsImage from "@/assets/infoEs.png";
import {
  UpdateChronopostPreferencesDto,
  UpdateColissimoCarrierPreferences,
  UpdateDpdCarrierPreferencesDto,
  UpdateDhlCarrierPreferencesDto,
  UpdateMondialRelayCarrierPreferencesDto,
} from "@babackapp/private-api-client/types";

export enum CarrierCodePickable {
  COLISSIMO = "colissimo",
  MONDIAL_RELAY = "mondial-relay",
  UPS_STANDARD = "ups-standard",
  UPS_SAVER = "ups-saver",
  UPS_GROUND = "ups-ground",
  DHL_EXPRESS = "dhl-express",
  DHL_ECONOMY_SELECT = "dhl-economy-select",
  DHL_PARCEL_DE = "dhl-parcel-de",
  CHRONOPOST = "chronopost",
  DPD = "dpd",
  DPD_CH = "dpd-ch",
  SWISS_POST = "swiss-post",
  FEDEX = "fedex",
  NO_PHYSICAL_RETURN = "no-physical-return",
}

export type CarrierCodePickableType = `${CarrierCodePickable}`;

export type CredentialsType =
  | UpdateChronopostPreferencesDto
  | UpdateColissimoCarrierPreferences
  | UpdateDpdCarrierPreferencesDto
  | UpdateDhlCarrierPreferencesDto
  | UpdateMondialRelayCarrierPreferencesDto;

export type FieldInfo = {
  placeholder: string | boolean;
  label: string;
  type: "text" | "password" | "number" | "boolean" | "select";
  required: boolean;
  options?: { value: string; label: string }[];
  /**
   * Short note rendered under the input. Use it when the label alone cannot
   * say where the value comes from — DHL Parcel DE asks for two unrelated
   * pairs of credentials, and a merchant who put their account login in the
   * API key field spent a week on a 401 (BBS-330).
   */
  hint?: string;
};

export type ConnectionInfo = {
  [fieldKey: string]: FieldInfo;
};

type CarrierCredentialFields = {
  value: string;
  label: string;
  connectionInfo: ConnectionInfo;
};
export const useCarriersVariables = () => {
  const t = useTranslation();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const hasFedexFlag = useShopFlag("fedex-carrier");
  const infoImageUrlByLocale: {
    [key: string]: string;
  } = {
    fr: ColissimoInfoFrImage,
    "fr-FR": ColissimoInfoFrImage,
    en: ColissimoInfoEnImage,
    "en-US": ColissimoInfoEnImage,
    es: ColissimoInfoEsImage,
    "es-ES": ColissimoInfoEsImage,
    de: ColissimoInfoEnImage,
    "de-DE": ColissimoInfoEnImage,
  };

  const passwordField: FieldInfo = {
    placeholder: "*****************",
    label: t("carrier.securityKey"),
    type: "password",
    required: true,
  };

  const idPlaceholder = t("carrier.idPlaceholder");
  const idLabel = t("carrier.idLabel");
  const shipperAccountNumberLabel = t("carrier.shipperAccountNumber");

  const listCarriers: CarrierCredentialFields[] = [
    {
      value: CarrierCodePickable.COLISSIMO,
      label: "Colissimo",
      connectionInfo: {
        accountId: {
          placeholder: idPlaceholder,
          label: idLabel,
          type: "text",
          required: true,
        },
        password: passwordField,
        SLS: {
          placeholder: true,
          label: t("carrier.SLSAccount"),
          type: "boolean",
          required: false,
        },
      },
    },
    {
      value: CarrierCodePickable.MONDIAL_RELAY,
      label: "Mondial Relay",
      connectionInfo: {
        brand: {
          placeholder: t("carrier.brandPlaceholder"),
          label: t("carrier.brandLabel"),
          type: "text",
          required: true,
        },
        login: {
          placeholder: idPlaceholder,
          label: t("carrier.login") + " (API V2)",
          type: "text",
          required: true,
        },
        password: {
          placeholder: "*****************",
          label: t("settings.carriers.password") + " (API V2)",
          type: "password",
          required: true,
        },
        securityKey: {
          placeholder: "*****************",
          label: t("carrier.securityKey") + " (API V1)",
          type: "password",
          required: true,
        },
      },
    },
    {
      value: CarrierCodePickable.DHL_EXPRESS,
      label: "DHL Express",
      connectionInfo: {
        webSiteId: {
          placeholder: idPlaceholder,
          label: t("carrier.websiteId"),
          type: "text",
          required: true,
        },
        securityKey: passwordField,
        shipperAccountNumber: {
          placeholder: idPlaceholder,
          label: shipperAccountNumberLabel,
          type: "number",
          required: true,
        },
      },
    },
    {
      value: CarrierCodePickable.DHL_ECONOMY_SELECT,
      label: "DHL Economy Select",
      connectionInfo: {
        webSiteId: {
          placeholder: idPlaceholder,
          label: t("carrier.websiteId"),
          type: "text",
          required: true,
        },
        securityKey: passwordField,
        shipperAccountNumber: {
          placeholder: idPlaceholder,
          label: shipperAccountNumberLabel,
          type: "number",
          required: true,
        },
      },
    },
    {
      value: CarrierCodePickable.DHL_PARCEL_DE,
      label: "DHL Parcel DE (Deutsche Post)",
      connectionInfo: {
        // Two unrelated credential pairs: the API key/secret come from the
        // developer portal, the username/password from the business portal.
        // Keys are DHL-specific rather than the shared carrier.appClientId,
        // which FedEx also uses.
        clientId: {
          placeholder: "a1b2c3d4-...",
          label: t("carrier.dhlParcelDe.apiKey"),
          hint: t("carrier.dhlParcelDe.apiKeyHint"),
          type: "text",
          required: true,
        },
        clientSecret: {
          placeholder: "*****************",
          label: t("carrier.dhlParcelDe.apiSecret"),
          type: "password",
          required: true,
        },
        username: {
          placeholder: idPlaceholder,
          label: t("carrier.dhlParcelDe.portalUsername"),
          hint: t("carrier.dhlParcelDe.portalHint"),
          type: "text",
          required: true,
        },
        password: {
          placeholder: "*****************",
          label: t("carrier.dhlParcelDe.portalPassword"),
          type: "password",
          required: true,
        },
        // Advanced options (optional)
        receiverId: {
          placeholder: "deu",
          label: t("carrier.dhlParcelDe.receiverId"),
          hint: t("carrier.dhlParcelDe.receiverIdHint"),
          type: "text",
          required: false,
        },
      },
    },
    {
      value: CarrierCodePickable.UPS_STANDARD,
      label: "UPS Standard",
      connectionInfo: {
        accountNumber: {
          placeholder: idPlaceholder,
          label: shipperAccountNumberLabel,
          type: "text",
          required: false,
        },
      },
    },
    {
      value: CarrierCodePickable.UPS_SAVER,
      label: "UPS Saver",
      connectionInfo: {
        accountNumber: {
          placeholder: idPlaceholder,
          label: shipperAccountNumberLabel,
          type: "text",
          required: true,
        },
      },
    },
    {
      value: CarrierCodePickable.UPS_GROUND,
      label: "UPS Ground",
      connectionInfo: {
        accountNumber: {
          placeholder: idPlaceholder,
          label: shipperAccountNumberLabel,
          type: "text",
          required: true,
        },
      },
    },
    // Chronopost is configured DB-side only (not merchant self-serve).
    // The backend supports the `chronopostService` values `reverse`,
    // `shop2shop` and `2shopretour-elabel` (E-Label) — set them via DB.
    // {
    //   value: CarrierCodePickable.CHRONOPOST,
    //   label: "Chronopost",
    //   connectionInfo: {
    //     accountNumber: {
    //       placeholder: idPlaceholder,
    //       label: shipperAccountNumberLabel,
    //       type: "text",
    //       required: true,
    //     },
    //     password: passwordField,
    //     subAccount: {
    //       placeholder: "",
    //       label: t("carrier.subAccount"),
    //       type: "text",
    //       required: false,
    //     },
    //   },
    // },
    {
      value: CarrierCodePickable.DPD,
      label: "DPD",
      connectionInfo: {
        userid: {
          placeholder: idPlaceholder,
          label: idLabel,
          type: "text",
          required: true,
        },
        password: passwordField,
        customerCountryCode: {
          placeholder: t("carrier.customerCountryCodePlaceholder"),
          label: t("carrier.customerCountryCode"),
          type: "text",
          required: true,
        },
        customerCenterNumber: {
          placeholder: t("carrier.customerCenterNumberPlaceholder"),
          label: t("carrier.customerCenterNumber"),
          type: "text",
          required: true,
        },
        customerNumber: {
          placeholder: t("carrier.customerNumberPlaceholder"),
          label: t("carrier.customerNumber"),
          type: "text",
          required: true,
        },
        returnType: {
          placeholder: "",
          label: "Return Type",
          type: "select",
          required: true,
          options: [
            { value: "Prepared", label: "Prepared" },
            { value: "OnDemand", label: "OnDemand" },
          ],
        },
      },
    },
    {
      value: CarrierCodePickable.DPD_CH,
      label: "DPD Suisse",
      connectionInfo: {
        username: {
          placeholder: idPlaceholder,
          label: idLabel,
          type: "text",
          required: true,
        },
        password: passwordField,
        invoicingNumber: {
          placeholder: t("carrier.customerNumberPlaceholder"),
          label: "Invoicing Number",
          type: "text",
          required: true,
        },
      },
    },
    {
      value: CarrierCodePickable.SWISS_POST,
      label: "Swiss Post (La Poste Suisse)",
      connectionInfo: {
        // The client comes from developer.apis.post.ch, the franking licence
        // from the Swiss Post contract: say so, the DHL Parcel DE form did not
        // and a merchant mixed up the two sources for a week (BBS-330).
        clientId: {
          placeholder: "a1b2c3d4-...",
          label: t("carrier.swissPost.clientId"),
          hint: t("carrier.swissPost.clientIdHint"),
          type: "text",
          required: true,
        },
        clientSecret: {
          placeholder: "*****************",
          label: t("carrier.swissPost.clientSecret"),
          // Same client, optional second subscription: without it, return
          // statuses fall back to TrackingMore, which is billed.
          hint: t("carrier.swissPost.trackingHint"),
          type: "password",
          required: true,
        },
        frankingLicense: {
          placeholder: "60001234",
          label: t("carrier.swissPost.frankingLicense"),
          hint: t("carrier.swissPost.frankingLicenseHint"),
          type: "text",
          required: true,
        },
        service: {
          placeholder: "",
          label: t("carrier.swissPost.service"),
          type: "select",
          required: true,
          options: [
            { value: "ECO", label: "PostPac Economy" },
            { value: "PRI", label: "PostPac Priority" },
          ],
        },
        domicilePostOffice: {
          placeholder: "3030 Bern",
          label: t("carrier.swissPost.domicilePostOffice"),
          hint: t("carrier.swissPost.domicilePostOfficeHint"),
          type: "text",
          required: false,
        },
        isDev: {
          placeholder: false,
          label: t("carrier.swissPost.testMode"),
          hint: t("carrier.swissPost.testModeHint"),
          type: "boolean",
          required: false,
        },
      },
    },
    {
      value: CarrierCodePickable.FEDEX,
      label: "FedEx",
      connectionInfo: {
        clientId: {
          placeholder: t("carrier.appClientId"),
          label: t("carrier.appClientId"),
          type: "text",
          required: true,
        },
        clientSecret: {
          placeholder: t("carrier.appClientSecret"),
          label: t("carrier.appClientSecret"),
          type: "password",
          required: true,
        },
        accountNumber: {
          placeholder: idPlaceholder,
          label: shipperAccountNumberLabel,
          type: "text",
          required: true,
        },
        shipperCountryCode: {
          placeholder: "Shipper Country Code (e.g., FR, US) (optional)",
          label: "Shipper Country Code",
          type: "text",
          required: false,
        },
        isDev: {
          placeholder: false,
          label: "Dev Mode (use FedEx sandbox)",
          type: "boolean",
          required: true,
        },
      },
    },
    {
      value: CarrierCodePickable.NO_PHYSICAL_RETURN,
      label: t("carrier.noPhysicalReturn"),
      connectionInfo: {},
    },
  ];

  const canUseFedex = isSuperAdmin || hasFedexFlag;

  return {
    listCarriers: canUseFedex
      ? listCarriers
      : listCarriers.filter(
          (carrier) => carrier.value !== CarrierCodePickable.FEDEX,
        ),
    infoImageUrlByLocale,
  };
};
